import { createApi, fakeBaseQuery, type QueryReturnValue } from '@reduxjs/toolkit/query/react';

import type { AppDependencies } from '../../infrastructure/appDependencies';
import { toAppError } from '../../utils/backendGatewayError';
import type { AppError } from '../../utils/firebaseErrors';
import type {
  ListRejectedReportPurgeAuditHistoryArgs,
  RejectedReportPurgeAuditHistory,
  RejectedReportPurgePolicy,
  RetentionGovernanceBackendGateway,
  UpdateRejectedReportPurgePolicyArgs,
} from './retentionGovernanceGateway';

const getRetentionGovernanceBackend = (extra: unknown): RetentionGovernanceBackendGateway => {
  if (extra && typeof extra === 'object' && 'retentionGovernanceBackend' in extra) {
    return (extra as AppDependencies).retentionGovernanceBackend;
  }

  throw new Error('App Runtime dependencies are not configured for retention governance API');
};

export const retentionGovernanceApi = createApi({
  reducerPath: 'retentionGovernanceApi',
  baseQuery: fakeBaseQuery<AppError>(),
  tagTypes: ['RejectedReportPurgePolicy', 'RejectedReportPurgeAuditHistory'],
  keepUnusedDataFor: 30,
  endpoints: (builder) => ({
    getRejectedReportPurgePolicy: builder.query<RejectedReportPurgePolicy, void>({
      async queryFn(
        _args,
        { extra }
      ): Promise<QueryReturnValue<RejectedReportPurgePolicy, AppError, {}>> {
        try {
          const data = await getRetentionGovernanceBackend(extra).getRejectedReportPurgePolicy();
          return { data };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
      providesTags: ['RejectedReportPurgePolicy'],
    }),
    updateRejectedReportPurgePolicy: builder.mutation<
      RejectedReportPurgePolicy,
      UpdateRejectedReportPurgePolicyArgs
    >({
      async queryFn(
        args,
        { extra }
      ): Promise<QueryReturnValue<RejectedReportPurgePolicy, AppError, {}>> {
        try {
          const data =
            await getRetentionGovernanceBackend(extra).updateRejectedReportPurgePolicy(args);
          return { data };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
      invalidatesTags: (result) =>
        result ? ['RejectedReportPurgePolicy', 'RejectedReportPurgeAuditHistory'] : [],
    }),
    getRejectedReportPurgeAuditHistory: builder.query<
      RejectedReportPurgeAuditHistory,
      ListRejectedReportPurgeAuditHistoryArgs
    >({
      async queryFn(
        args,
        { extra }
      ): Promise<QueryReturnValue<RejectedReportPurgeAuditHistory, AppError, {}>> {
        try {
          const data =
            await getRetentionGovernanceBackend(extra).listRejectedReportPurgeAuditHistory(args);
          return { data };
        } catch (error) {
          return { error: toAppError(error) };
        }
      },
      providesTags: ['RejectedReportPurgeAuditHistory'],
    }),
  }),
});

export const {
  useGetRejectedReportPurgePolicyQuery,
  useUpdateRejectedReportPurgePolicyMutation,
  useGetRejectedReportPurgeAuditHistoryQuery,
} = retentionGovernanceApi;
